import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { services, stats, values } from '@/data/index';
import { fadeInUp, staggerContainer, staggerItem, hoverLift, springPresets } from '@/lib/motion';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Building2, FileText, TrendingUp, Users, Shield, Lightbulb, Mail, Phone, MapPin, Send } from 'lucide-react';

const iconMap = {
  Building2,
  FileText,
  TrendingUp,
  Users,
  Shield,
  Lightbulb
};

function ServiceCard({ service }: { service: typeof services[0] }) {
  const Icon = iconMap[service.icon as keyof typeof iconMap];
  
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="bg-card rounded-xl p-8 border border-border"
    >
      <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center mb-6">
        {Icon && <Icon className="w-7 h-7 text-accent" />}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-foreground">{service.title}</h3>
      <p className="text-muted-foreground leading-relaxed">{service.description}</p>
    </motion.div>
  );
}

function ValueCard({ value }: { value: typeof values[0] }) {
  const Icon = iconMap[value.icon as keyof typeof iconMap];
  
  return (
    <motion.div
      variants={staggerItem}
      className="text-center"
    >
      <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
        {Icon && <Icon className="w-8 h-8 text-accent" />}
      </div>
      <h3 className="text-2xl font-semibold mb-4 text-primary-foreground">{value.title}</h3>
      <p className="text-primary-foreground/80 leading-relaxed max-w-sm mx-auto">{value.description}</p>
    </motion.div>
  );
}

function StatCard({ stat }: { stat: typeof stats[0] }) {
  return (
    <motion.div
      variants={staggerItem}
      className="text-center"
    >
      <div className="text-5xl font-bold text-accent mb-2">
        {stat.value}{stat.suffix}
      </div>
      <div className="text-muted-foreground font-medium">{stat.label}</div>
    </motion.div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const nosotrosRef = useRef(null);
  const serviciosRef = useRef(null);
  const valoresRef = useRef(null);
  const contactoRef = useRef(null);
  const statsRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const nosotrosInView = useInView(nosotrosRef, { once: true, amount: 0.3 });
  const serviciosInView = useInView(serviciosRef, { once: true, amount: 0.2 });
  const valoresInView = useInView(valoresRef, { once: true, amount: 0.3 });
  const contactoInView = useInView(contactoRef, { once: true, amount: 0.2 });
  const statsInView = useInView(statsRef, { once: true, amount: 0.3 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Layout>
      <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.TRAFFIC_4} 
            alt="Ciudad digital" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/70 to-primary/90" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-primary-foreground mb-6 leading-tight">
              Transformación Digital para el Sector Público
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90 mb-12 leading-relaxed max-w-3xl mx-auto">
              Soluciones tecnológicas integrales que optimizan la gestión y el desempeño de las entidades públicas colombianas
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground text-lg px-8 py-6 rounded-xl"
              >
                <a href="#servicios">Nuestros Servicios</a>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 text-lg px-8 py-6 rounded-xl"
              >
                <a href="#contacto">Contáctanos</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="nosotros" ref={nosotrosRef} className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={nosotrosInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
              transition={springPresets.gentle}
            >
              <img 
                src={IMAGES.SERVICES_3} 
                alt="Equipo profesional" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={nosotrosInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
              transition={springPresets.gentle}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Quiénes Somos
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                SOLUCIONES INNOVADORAS S.I. S.A.S. es una empresa colombiana especializada en la transformación digital del sector público. Contamos con un equipo multidisciplinario de profesionales con profundo conocimiento del sector público y privado.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Nuestra misión es proporcionar soluciones tecnológicas integrales que optimicen la gestión y el desempeño de las entidades públicas, con especial énfasis en la gestión de cobranza y multas e infracciones de tránsito.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Innovación Constante</h3>
                    <p className="text-muted-foreground">Tecnologías de vanguardia para la modernización del sector público</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Transparencia Total</h3>
                    <p className="text-muted-foreground">Procesos claros y confiables en todas nuestras operaciones</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Eficiencia Comprobada</h3>
                    <p className="text-muted-foreground">Resultados medibles que mejoran el desempeño institucional</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            ref={statsRef}
            variants={staggerContainer}
            initial="hidden"
            animate={statsInView ? "visible" : "hidden"}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24"
          >
            {stats.map(stat => (
              <StatCard key={stat.id} stat={stat} />
            ))}
          </motion.div>
        </div>
      </section>

      <section id="servicios" ref={serviciosRef} className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={serviciosInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Nuestros Servicios
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Soluciones tecnológicas especializadas para la transformación digital del sector público
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={serviciosInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map(service => (
              <motion.div key={service.id} variants={staggerItem}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="valores" ref={valoresRef} className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={valoresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
              Nuestros Valores
            </h2>
            <p className="text-xl text-primary-foreground/80 max-w-3xl mx-auto">
              Los pilares que guían nuestro compromiso con la excelencia
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={valoresInView ? "visible" : "hidden"}
            className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto"
          >
            {values.map(value => (
              <ValueCard key={value.id} value={value} />
            ))}
          </motion.div>
        </div>
      </section>

      <section id="contacto" ref={contactoRef} className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={contactoInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Contáctanos
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Estamos listos para impulsar la transformación digital de su entidad
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={contactoInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
              transition={springPresets.gentle}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-foreground mb-2 block">Nombre Completo</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ingrese su nombre"
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-foreground mb-2 block">Correo Electrónico</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="correo@ejemplo.com"
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-foreground mb-2 block">Teléfono</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+57 300 123 4567"
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-foreground mb-2 block">Mensaje</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Cuéntenos sobre su proyecto o necesidad"
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Mensaje
                </Button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={contactoInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
              transition={springPresets.gentle}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl font-semibold text-foreground mb-6">Información de Contacto</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground mb-1">Correo Electrónico</div>
                      <a href="mailto:contacto@solucionesinnovadoras.com" className="text-muted-foreground hover:text-accent transition-colors">
                        contacto@solucionesinnovadoras.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground mb-1">Teléfono</div>
                      <a href="tel:+573001234567" className="text-muted-foreground hover:text-accent transition-colors">
                        +57 300 123 4567
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground mb-1">Ubicación</div>
                      <p className="text-muted-foreground">
                        Bogotá D.C., Colombia
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 rounded-xl p-8 border border-border">
                <h4 className="font-semibold text-foreground mb-4">Horario de Atención</h4>
                <div className="space-y-2 text-muted-foreground">
                  <p>Lunes a Viernes: 8:00 AM - 6:00 PM</p>
                  <p>Sábados: 9:00 AM - 1:00 PM</p>
                  <p>Domingos y Festivos: Cerrado</p>
                </div>
              </div>

              <div className="aspect-video bg-muted rounded-xl overflow-hidden border border-border">
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <MapPin className="w-12 h-12" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}